import { useState } from 'react';
import { isAxiosError } from 'axios';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { InfoRow } from '@/components/InfoRow';
import { WizardStepper } from '@/components/WizardStepper';
import type { AdminUserDetail, AdminUserOptions, SaveAdminUserPayload } from '@/api/adminUsers';
import { ProfileFields, SsoFields, RolesFields } from './AdminFields';
import { usePermissionCatalog } from './usePermissionCatalog';
import {
  createEmptyAdminState,
  adminStateToPayload,
  validateAdminStep,
  validateAdminPassword,
  REQUIRED_ADMIN_FIELD_LABELS,
  type AdminFormState,
} from './adminFormState';

const STEPS = ['Информация профиля', 'Единый вход', 'Роли и права', 'Проверка'] as const;
const LAST_STEP = STEPS.length;

/**
 * Мастер создания администратора — по образцу мастера создания клиента
 * (см. pages/client-create): состояние живёт здесь целиком, на бэк ничего не
 * уходит до кнопки «Создать» на последнем шаге. Вперёд — только через «Далее»
 * с проверкой обязательных полей шага, назад — кликом по пройденному шагу.
 */
export function AdminCreateWizard({
  options,
  onCreate,
  onCancel,
}: {
  options: AdminUserOptions | null;
  onCreate: (payload: SaveAdminUserPayload) => Promise<AdminUserDetail>;
  onCancel: () => void;
}) {
  const [state, setState] = useState<AdminFormState>(createEmptyAdminState);
  const [step, setStep] = useState(1);
  const [maxReached, setMaxReached] = useState(1);
  const [attemptedNext, setAttemptedNext] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { catalog, error: catalogError } = usePermissionCatalog();

  const missingFields = validateAdminStep(step, state);
  const fieldErrors = attemptedNext ? missingFields : [];

  const goToStep = (target: number) => {
    if (target > maxReached) return;
    setAttemptedNext(false);
    setStep(target);
  };

  const goNext = () => {
    if (missingFields.length > 0) {
      setAttemptedNext(true);
      return;
    }
    // Короткий пароль дальше шага «Единый вход» не пускает — то же правило, что на бэке.
    const passwordError = validateAdminPassword(state);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    setError(null);
    setAttemptedNext(false);
    const next = Math.min(step + 1, LAST_STEP);
    setStep(next);
    setMaxReached((m) => Math.max(m, next));
  };

  const goBack = () => {
    setAttemptedNext(false);
    setStep((s) => Math.max(1, s - 1));
  };

  const create = async () => {
    setError(null);
    setSubmitting(true);
    try {
      // login = null: у нового администратора его ещё нет, сервер подставит почту.
      await onCreate(adminStateToPayload(state, null));
    } catch (err) {
      const detail = isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : null;
      setError(detail || 'Не удалось создать администратора.');
    } finally {
      setSubmitting(false);
    }
  };

  const fieldsProps = { state, onChange: setState, options, disabled: submitting, fieldErrors };
  const statusLabel = options?.statuses.find((s) => s.value === state.status)?.label ?? state.status;
  const roleNames = (options?.roles ?? [])
    .filter((r) => state.roleIds.includes(Number(r.value)))
    .map((r) => r.label);
  const mt1Label = options?.mt1Servers.find((s) => s.value === state.mt1Server)?.label ?? null;

  return (
    <div className="wizard">
      {submitting && (
        <div className="wizard__submitting-overlay" aria-live="polite">
          <Spinner label="Создание администратора…" />
          <p className="wizard__submitting-hint">
            Может занять до минуты — идёт регистрация на сервере авторизации. Не закрывайте страницу.
          </p>
        </div>
      )}

      <WizardStepper steps={STEPS} current={step} maxReached={maxReached} onStepClick={goToStep} />

      <div className="wizard__body">
        {attemptedNext && missingFields.length > 0 && (
          <p className="field-error">
            Заполните обязательные поля: {missingFields.map((k) => REQUIRED_ADMIN_FIELD_LABELS[k]).join(', ')}.
          </p>
        )}

        {step === 1 && <ProfileFields {...fieldsProps} />}
        {step === 2 && <SsoFields {...fieldsProps} login={null} />}
        {step === 3 && <RolesFields {...fieldsProps} catalog={catalog} />}
        {step === 4 && (
          <div className="detail__grid-2">
            <div className="card">
              <h2 className="detail__subtitle">Информация профиля</h2>
              <dl className="info-list">
                <InfoRow label="Название" value={state.name || null} />
                <InfoRow label="Фамилия" value={state.lastName || null} />
                <InfoRow label="Имя" value={state.firstName || null} />
                <InfoRow label="Отчество" value={state.surname || null} />
                <InfoRow label="Электронная почта" value={state.email || null} />
                <InfoRow label="Статус кабинета" value={statusLabel} />
              </dl>
            </div>

            <div className="card">
              <h2 className="detail__subtitle">Доступ</h2>
              <dl className="info-list">
                <InfoRow label="Единый логин" value={state.email || null} />
                <InfoRow label="Пароль" value={state.password ? 'Задан вручную' : 'Сгенерируется автоматически'} />
                <InfoRow label="Синхронизация с серверами" value={mt1Label} />
                <InfoRow label="Роли" value={roleNames.join(', ') || null} />
                <InfoRow label="Персональных прав" value={String(state.permissions.length)} />
              </dl>
            </div>
          </div>
        )}

        {(error || catalogError) && <p className="field-error">{error ?? catalogError}</p>}
      </div>

      <div className="wizard__footer">
        <button
          type="button"
          className="btn btn--secondary"
          onClick={step === 1 ? onCancel : goBack}
          disabled={submitting}
        >
          {step === 1 ? (
            'Отмена'
          ) : (
            <>
              <Icon name="chevronLeft" size={14} /> Назад
            </>
          )}
        </button>
        {step < LAST_STEP ? (
          <button type="button" className="btn btn--primary" onClick={goNext}>
            Далее <Icon name="chevronRight" size={14} />
          </button>
        ) : (
          <button type="button" className="btn btn--primary" onClick={create} disabled={submitting}>
            <Icon name="check" size={15} /> {submitting ? 'Создание…' : 'Создать'}
          </button>
        )}
      </div>
    </div>
  );
}
